import { createContext, useContext, useMemo, type ReactNode } from 'react';

/**
 * Shared opener for the encounter side drawer slot owned by `CarePlanView`. Both the
 * timeline `+` button and the Encounters records-list `Add` button reach into the same
 * drawer slot (the bottom `ResizablePanel` on desktop, the bottom `Sheet` on mobile) via
 * this context — the alternative was prop-drilling through `children`.
 */
type EncounterDrawerContextValue = {
  openAddEncounter: () => void;
  openAddLabOrder: () => void;
  openEncounter: (encounter: { id: string; title: string }) => void;
  openLabOrder: (labOrder: { id: string; title: string }) => void;
  /** Opens the fax viewer dialog (not the drawer slot — a fax is a read-only PDF). No title:
   *  the dialog derives its own from the fetched attachment. */
  openFax: (fax: { id: string }) => void;
  closeDrawer: () => void;
  /** Hides the timeline card. A record rendering inline in the card is closed with it, so the URL
   *  never claims a record is open that nothing on screen shows. */
  collapseTimeline: () => void;
};

const EncounterDrawerContext = createContext<EncounterDrawerContextValue | undefined>(undefined);

type ProviderProps = EncounterDrawerContextValue & { children: ReactNode };

export const EncounterDrawerProvider = ({
  openAddEncounter,
  openAddLabOrder,
  openEncounter,
  openLabOrder,
  openFax,
  closeDrawer,
  collapseTimeline,
  children,
}: ProviderProps): React.ReactNode => {
  const value = useMemo<EncounterDrawerContextValue>(
    () => ({ openAddEncounter, openAddLabOrder, openEncounter, openLabOrder, openFax, closeDrawer, collapseTimeline }),
    [openAddEncounter, openAddLabOrder, openEncounter, openLabOrder, openFax, closeDrawer, collapseTimeline],
  );
  return <EncounterDrawerContext.Provider value={value}>{children}</EncounterDrawerContext.Provider>;
};

// oxlint-disable-next-line react/only-export-components
export const useEncounterDrawer = (): EncounterDrawerContextValue => {
  const ctx = useContext(EncounterDrawerContext);
  if (!ctx) throw new Error('useEncounterDrawer must be used inside <EncounterDrawerProvider>');
  return ctx;
};
